import { useCallback, useEffect, useRef } from "react";
import { FragmentsManager } from "@thatopen/components";
import { Mark } from "@thatopen/components-front";
import { Box3, Vector3 } from "three";
import useViewerContext from "@/services/context/viewer/instance/use-viewer-context.ts";
import SpaceTypo from "@/models/enums/space-typo.ts";
import { AssociatedObjectAndTypo } from "../models/associated-object-and-typo.ts";
import { ValidationMode } from "../types/validation-mode-type.ts";
import validationsTypes from "../models/validations-type.ts";
import thermalsType from "../models/thermals-type.ts";

const getShellLabel = (shell: AssociatedObjectAndTypo, mode: ValidationMode): string => {
    if (mode === "thermal") {
        return thermalsType.find(type => type.keyName === shell.thermalTypo)?.label ?? "Aucun";
    }

    let type = shell.shellTypo;

    if (type === SpaceTypo.Balcon || type === SpaceTypo.Terrasse) {
        type = SpaceTypo.SUexterieure;
    }

    return validationsTypes.find(item => item.keyName === type)?.label ?? SpaceTypo[type] ?? "Aucun";
};

const useShellLabels = (shells: AssociatedObjectAndTypo[], mode: ValidationMode) => {
    const { viewer, world, isLoading } = useViewerContext();
    const labelsRef = useRef(new Map<string, Mark>());
    const modeRef = useRef(mode);

    const updateShellLabel = useCallback((shell: AssociatedObjectAndTypo) => {
        if (!shell._3D) {
            return;
        }

        const label = labelsRef.current.get(shell._3D);

        if (label) {
            label.three.element.textContent = getShellLabel(shell, modeRef.current);
        }
    }, []);

    useEffect(() => {
        modeRef.current = mode;
        shells.forEach(updateShellLabel);
    }, [mode, shells, updateShellLabel]);

    useEffect(() => {
        if (isLoading || !viewer || !world) {
            return undefined;
        }

        const labels = labelsRef.current;
        let cancelled = false;

        const createLabels = async () => {
            const visibleShells = shells.filter(shell => shell._3D != null);
            const guids = visibleShells.map(shell => shell._3D!);
            const bounds = new Map<string, Box3>();
            const fragments = viewer.getComponents().get(FragmentsManager);

            // Resolve all shells in one request per model, including split geometries.
            await Promise.all(Array.from(fragments.list.values()).map(async model => {
                const localIds = await model.getLocalIdsByGuids(guids);

                if (cancelled) {
                    return;
                }

                const matchedIds: number[] = [];
                const matchedGuids: string[] = [];

                localIds.forEach((localId, index) => {
                    if (localId != null) {
                        matchedIds.push(localId);
                        matchedGuids.push(guids[index]);
                    }
                });

                if (matchedIds.length === 0) {
                    return;
                }

                const boxes = await model.getBoxes(matchedIds);

                boxes.forEach((box, index) => {
                    const guid = matchedGuids[index];
                    const existing = bounds.get(guid);

                    if (existing) {
                        existing.union(box);
                    } else {
                        bounds.set(guid, box.clone());
                    }
                });
            }));

            if (cancelled) {
                return;
            }

            visibleShells.forEach(shell => {
                const guid = shell._3D!;
                const box = bounds.get(guid);

                if (!box || box.isEmpty() || labels.has(guid)) {
                    return;
                }

                const element = document.createElement("div");
                element.textContent = getShellLabel(shell, modeRef.current);
                element.className = "rounded border border-gray-300 bg-white/90 px-2 py-1 text-xs font-semibold text-gray-900 shadow-sm whitespace-nowrap";
                element.style.pointerEvents = "none";
                element.style.userSelect = "none";

                const label = new Mark(world, element);
                label.three.position.copy(box.getCenter(new Vector3()));
                labels.set(guid, label);
            });
        };

        createLabels().catch(error => {
            if (!cancelled) {
                console.error("Erreur lors du chargement des étiquettes des coques", error);
            }
        });

        return () => {
            cancelled = true;
            labels.forEach(label => {
                label.dispose();
            });
            labels.clear();
        };
    }, [isLoading, shells, viewer, world]);

    return updateShellLabel;
};

export default useShellLabels;
